import { http, type HttpRequestOptions } from './request'
import type {
  ApiResponse,
  LoginParams,
  LoginResult,
  User,
  SwitchRoleResult,
  DashboardPersonalTemplate,
  DashboardWorkspaceV2
} from '@/types'

export type AuthRequestOptions = HttpRequestOptions

/** 单个主题的自定义背景引用（与后端 BackgroundRef 对齐） */
export type UiBackgroundRef =
  | { type: 'preset'; presetId: string }
  | { type: 'upload'; fileKey: string }

/** 用户界面偏好：主题选择 + 每主题自定义背景（按账号 × 激活角色存服务端） */
export interface UiPreferencePayload {
  themeCode: string | null
  backgrounds: Record<string, UiBackgroundRef>
}

/** 背景图上传结果 */
export interface UiBackgroundUploadResult {
  fileKey: string
  url: string
}

type TokenPair = {
  accessToken: string
  refreshToken: string
}

export const authApi = {
  login(params: LoginParams, options?: AuthRequestOptions): Promise<ApiResponse<LoginResult>> {
    return http.post<LoginResult>('/auth/login', params, { ...options, skipAuth: true })
  },

  logout(options?: AuthRequestOptions): Promise<ApiResponse<void>> {
    return http.post<void>('/auth/logout', undefined, options)
  },

  getCurrentUser(options?: AuthRequestOptions): Promise<ApiResponse<User>> {
    return http.get<User>('/auth/me', undefined, options)
  },

  /** 切换当前激活角色：后端校验成员资格后以目标角色重签访问令牌（refreshToken 保持不变） */
  switchRole(roleCode: string, options?: AuthRequestOptions): Promise<ApiResponse<SwitchRoleResult>> {
    return http.post<SwitchRoleResult>('/auth/switch-role', { roleCode }, options)
  },

  getDashboardWorkspace(options?: AuthRequestOptions): Promise<ApiResponse<DashboardWorkspaceV2 | null>> {
    return http.get<DashboardWorkspaceV2 | null>('/auth/me/dashboard-workspace', undefined, options)
  },

  putDashboardWorkspace(
    payload: DashboardWorkspaceV2,
    options?: AuthRequestOptions
  ): Promise<ApiResponse<DashboardWorkspaceV2>> {
    return http.put<DashboardWorkspaceV2>('/auth/me/dashboard-workspace', payload, options)
  },

  getDashboardTemplates(options?: AuthRequestOptions): Promise<ApiResponse<DashboardPersonalTemplate[]>> {
    return http.get<DashboardPersonalTemplate[]>('/auth/me/dashboard-templates', undefined, options)
  },

  createDashboardTemplate(
    name: string,
    payload: DashboardWorkspaceV2,
    options?: AuthRequestOptions
  ): Promise<ApiResponse<DashboardPersonalTemplate>> {
    return http.post<DashboardPersonalTemplate>('/auth/me/dashboard-templates', { name, payload }, options)
  },

  renameDashboardTemplate(
    id: number,
    name: string,
    options?: AuthRequestOptions
  ): Promise<ApiResponse<DashboardPersonalTemplate>> {
    return http.put<DashboardPersonalTemplate>(`/auth/me/dashboard-templates/${id}`, { name }, options)
  },

  deleteDashboardTemplate(id: number, options?: AuthRequestOptions): Promise<ApiResponse<void>> {
    return http.delete<void>(`/auth/me/dashboard-templates/${id}`, options)
  },

  getUiPreferences(options?: AuthRequestOptions): Promise<ApiResponse<UiPreferencePayload>> {
    return http.get<UiPreferencePayload>('/auth/me/ui-preferences', undefined, options)
  },

  putUiPreferences(
    payload: UiPreferencePayload,
    options?: AuthRequestOptions
  ): Promise<ApiResponse<UiPreferencePayload>> {
    return http.put<UiPreferencePayload>('/auth/me/ui-preferences', payload, options)
  },

  uploadUiBackground(
    file: File,
    options?: AuthRequestOptions
  ): Promise<ApiResponse<UiBackgroundUploadResult>> {
    const form = new FormData()
    form.append('file', file)
    return http.postForm<UiBackgroundUploadResult>('/auth/me/ui-background', form, options)
  },

  refreshToken(
    refreshToken: string,
    options?: AuthRequestOptions
  ): Promise<ApiResponse<TokenPair>> {
    return http.post<TokenPair>('/auth/refresh', { refreshToken }, { ...options, skipAuth: true })
  },

  changePassword(
    oldPassword: string,
    newPassword: string,
    confirmPassword: string,
    options?: AuthRequestOptions
  ): Promise<ApiResponse<void>> {
    return http.post<void>(
      '/auth/me/change-password',
      { oldPassword, newPassword, confirmPassword },
      options
    )
  }
}
